import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PartnerSidebar from '../components/Sidebar';
import { useSidebar } from '../context/SidebarContext';
import { useAuth } from '../context/AuthContext';
import { listenToNotifications, markAsRead } from '../utils/notificationService';

function PartnerNotifications() {
  const { isMobile, isOpen, toggleMobileSidebar } = useSidebar();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    if (!currentUser?.uid) return;
    const unsub = listenToNotifications(currentUser.uid, 'partner', setNotifications);
    return () => unsub();
  }, [currentUser]);

  const handleNotificationClick = useCallback(async (notification) => {
    if (!notification.read) {
      await markAsRead(notification.id, 'partner');
    }
    if (notification.data?.queryId) {
      navigate('/partner/queries');
    }
  }, [navigate]);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = (now - date) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <PartnerSidebar />
      <div
        className="flex-1 overflow-y-auto transition-all duration-300"
        style={{ marginLeft: isMobile ? 0 : (isOpen ? '16rem' : '5rem') }}
      >
        <div className="p-8">
          <div className="flex items-center mb-8">
            {isMobile && (
              <button
                onClick={toggleMobileSidebar}
                className="mr-4 p-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:text-brand-golden"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            )}
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Notifications</h1>
              <p className="text-gray-500 mt-1">
                {notifications.filter(n => !n.read).length} unread
              </p>
            </div>
          </div>

          <div className="space-y-2">
            {notifications.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <p className="text-gray-500 text-lg font-medium">No notifications yet</p>
                <p className="text-gray-400 text-sm mt-1">Updates about your queries and account will appear here.</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`bg-white rounded-xl shadow-sm border p-4 cursor-pointer hover:shadow-md transition-all ${
                    !notification.read ? 'border-l-4 border-l-brand-golden' : 'border-gray-100'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      !notification.read ? 'bg-brand-golden/10' : 'bg-gray-100'
                    }`}>
                      <svg className={`w-5 h-5 ${!notification.read ? 'text-brand-golden' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className={`font-medium ${!notification.read ? 'text-gray-900' : 'text-gray-600'}`}>
                          {notification.title}
                        </h3>
                        {!notification.read && (
                          <span className="w-2 h-2 bg-brand-golden rounded-full flex-shrink-0"></span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">{notification.body || notification.message}</p>
                      <p className="text-xs text-gray-400 mt-2">{formatTime(notification.createdAt)}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PartnerNotifications;
